import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import Cookies from 'js-cookie';
import { Cookie, SiteAuth } from "../../constants";
import { onAppInit } from "../common/thunks";

const initialState = {
  isLoading: true,
  token: null,
  isTempUser: null,
  sessionId: uuidv4(),
  sitePasswordValidated: false
};

const userIdentifierToken = "";

export const onAuth0LoadingCompleted = createAsyncThunk(
  "chat/onAuth0LoadingCompleted",
  async ({ isAuthenticated, getAccessTokenFn }, { rejectWithValue }) => {
    try {
      let token = userIdentifierToken;
      
      if (isAuthenticated && getAccessTokenFn) {
        try {
          token = await getAccessTokenFn();
        } catch (error) {
          console.error("Failed to get access token:", error);
          // If token retrieval fails, still proceed but with empty token
          // This allows the app to continue but API calls will fail with 401
          token = userIdentifierToken;
        }
      }
      
      return {
        isAuthenticated,
        token,
      };
    } catch (error) {
      return rejectWithValue({ 
        message: "Failed to initialize authentication", 
        error: error.message 
      });
    }
  }
);

export const onSitePasswordValidated = createAsyncThunk(
  "chat/onSitePasswordValidated",
  () => {
    Cookies.set(Cookie.SiteAuth, SiteAuth.isAuthenticated);
    return;
  }
);

export const refreshAuthToken = createAsyncThunk(
  "auth/refreshAuthToken",
  async (getAccessTokenFn, { rejectWithValue }) => {
    try {
      if (!getAccessTokenFn) {
        return rejectWithValue({ message: 'No token refresh function available' });
      }
      const token = await getAccessTokenFn();
      return { token };
    } catch (error) {
      return rejectWithValue({ 
        message: 'Failed to refresh token', 
        error: error.message 
      });
    }
  }
);

const authSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(onAuth0LoadingCompleted.pending, (state) => {
      state = initialState;
    });

    builder.addCase(onAuth0LoadingCompleted.fulfilled, (state, { payload }) => {
      const { isAuthenticated, token } = payload;
      state.token = token;
      state.isTempUser = !isAuthenticated;
      state.isLoading = false;
    });

    builder.addCase(onAuth0LoadingCompleted.rejected, (state) => {
      state.token = userIdentifierToken;
      state.isTempUser = true;
      state.isLoading = false;
    });

    builder.addCase(onSitePasswordValidated.fulfilled, (state) => {
      state.sitePasswordValidated = true;
    });

    builder.addCase(onAppInit.fulfilled, (state, { payload }) => {
      const { sitePasswordValidated } = payload;
      state.sitePasswordValidated = sitePasswordValidated;
    });

    builder.addCase(refreshAuthToken.fulfilled, (state, { payload }) => {
      state.token = payload.token;
    });

    builder.addCase(refreshAuthToken.rejected, (state) => {
      // Token refresh failed, but don't clear the existing token
      // The user might need to re-authenticate
    });
  },
});

export default authSlice.reducer;
